/**
 * Digit drawing pad.
 *
 * Owns a canvas the user draws on and produces MNIST-compatible inputs. Styling
 * is intentionally minimal: a black background with a thick white stroke, so the
 * canvas already looks like an MNIST sample.
 */
import { canvasToInput } from './preprocess.ts';

export interface DrawPadOptions {
  /** Canvas edge length in CSS pixels. */
  size?: number;
  onInput?: () => void;
}

export class DrawPad {
  #canvas: HTMLCanvasElement;
  #context: CanvasRenderingContext2D;
  #size: number;
  #drawing = false;
  #onInput?: () => void;

  constructor(canvas: HTMLCanvasElement, options: DrawPadOptions = {}) {
    this.#canvas = canvas;
    this.#size = options.size ?? 280;
    this.#onInput = options.onInput;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('DrawPad: 2D canvas context unavailable');
    }
    this.#context = context;

    canvas.width = this.#size;
    canvas.height = this.#size;
    this.clear();

    canvas.addEventListener('pointerdown', (event) => this.#start(event));
    canvas.addEventListener('pointermove', (event) => this.#move(event));
    canvas.addEventListener('pointerup', () => this.#end());
    canvas.addEventListener('pointerleave', () => this.#end());
    canvas.addEventListener('pointercancel', () => this.#end());
  }

  get isBlank(): boolean {
    const { data } = this.#context.getImageData(0, 0, this.#size, this.#size);
    for (let i = 0; i < data.length; i += 4) {
      if ((data[i] ?? 0) > 20) {
        return false;
      }
    }
    return true;
  }

  clear(): void {
    this.#context.fillStyle = '#000000';
    this.#context.fillRect(0, 0, this.#size, this.#size);
    this.#onInput?.();
  }

  /** Returns a normalized 28x28 buffer, or null when the pad is empty. */
  toInput(): Float32Array | null {
    if (this.isBlank) {
      return null;
    }
    return canvasToInput(this.#context, { size: this.#size });
  }

  #point(event: PointerEvent): { x: number; y: number } {
    const rect = this.#canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * this.#size,
      y: ((event.clientY - rect.top) / rect.height) * this.#size,
    };
  }

  #start(event: PointerEvent): void {
    this.#drawing = true;
    this.#canvas.setPointerCapture(event.pointerId);

    const { x, y } = this.#point(event);
    this.#context.strokeStyle = '#ffffff';
    this.#context.lineWidth = this.#size * 0.05;
    this.#context.lineCap = 'round';
    this.#context.lineJoin = 'round';
    this.#context.beginPath();
    this.#context.moveTo(x, y);
    // A single tap must leave a mark, so draw a zero-length segment.
    this.#context.lineTo(x + 0.1, y + 0.1);
    this.#context.stroke();
  }

  #move(event: PointerEvent): void {
    if (!this.#drawing) {
      return;
    }
    const { x, y } = this.#point(event);
    this.#context.lineTo(x, y);
    this.#context.stroke();
  }

  #end(): void {
    if (!this.#drawing) {
      return;
    }
    this.#drawing = false;
    this.#context.closePath();
    this.#onInput?.();
  }
}
